import { Badge, Button, HStack } from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { chakraComponents, Select as ChakraSelect, type GroupBase, type SelectComponentsConfig } from 'chakra-react-select'
import React from 'react'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

export interface Option {
  value: string | number
  label: string
}

export type MultipleSelectDisplayType = 'default' | 'count'

// Component-specific props
export interface MultipleSelectFormControlComponentProps {
  placeholder?: string
  options?: Option[]
  isLoading?: boolean
  variant?: string
  size?: 'sm' | 'md' | 'lg'
  sx?: any
  w?: any
  // allow callers to control portal/position like in SelectionList
  menuPortalTarget?: HTMLElement | null
  menuPosition?: 'absolute' | 'fixed'
  chakraStyles?: any
  valueAsNumber?: boolean
  display?: MultipleSelectDisplayType
}

const customComponents = (display?: MultipleSelectDisplayType): SelectComponentsConfig<
  Option,
  true,
  GroupBase<Option>
> => display === 'count' ?
    ({
      ValueContainer: ({ children, ...props }) => {
        const count = props.getValue().length
        const totalCount = props.selectProps.options?.length
        const all = count === totalCount
        return <chakraComponents.ValueContainer {...props}>
          {all ? `Vše (${count})` : `${count} z ${totalCount} vybráno`}
        </chakraComponents.ValueContainer>
      },
      MenuList: ({ children, ...props }) => {
        const value = props.getValue()
        return <chakraComponents.MenuList {...props}>
          <HStack flexWrap={'wrap'} gap={1} pl={1} pb={1.5}>
            {value.map(v => (
              <Badge key={v.value} pl={1} pr={0} py={.5} borderRadius="md" colorScheme="blue">
                <HStack gap={0}>
                  <span>{v.label}</span>
                  <Button as={FontAwesomeIcon} icon={faClose}
                    size={'sm'} boxSize={3} variant='ghost' colorScheme='gray' p={1} cursor='pointer'
                    onClick={() => props.setValue([...value.filter(i => i.value !== v.value)], 'deselect-option')}
                    _hover={{ bg: 'none' }} />
                </HStack>
              </Badge>
            ))}
          </HStack>
          {children}
        </chakraComponents.MenuList>
      }
    })
    : {}

// value for form field is array of strings or numbers
export const MultipleSelectFormControlBase = React.forwardRef<
  HTMLDivElement,
  MultipleSelectFormControlComponentProps & FormFieldProps<string[] | number[], HTMLDivElement>
>((props, ref) => {
  const {
    // form props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // component props
    options = [],
    placeholder,
    size,
    variant,
    menuPortalTarget,
    menuPosition,
    chakraStyles,
    display
  } = props

  const selectedValues = (value ?? []).map(v => {
    const opt = options.find(o => o.value === v || String(o.value) === String(v))
    return opt ?? { label: String(v), value: v }
  })

  const allSelected = (value?.length ?? 0) === options.length

  return (
    <ChakraSelect
      name={name}
      isMulti
      options={options}
      value={selectedValues}
      onChange={(items) => {
        onChange(items.map(i => i.value) as string[] | number[])
        onBlur()
      }}
      ref={ref as any}
      isDisabled={isDisabled}
      placeholder={placeholder}
      variant={variant as any}
      size={size}
      menuPortalTarget={typeof window !== 'undefined' ? (menuPortalTarget ?? document.body) : undefined}
      menuPosition={menuPosition ?? 'fixed'}
      styles={{
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999,
        })
      }}
      chakraStyles={{
        inputContainer: (p: any) => ({ ...p, px: 0 }),
        valueContainer: (p: any) => ({ ...p, py: 0 }),
        container: (p: any) => ({ ...p, w: '100%', h: '100%' }),

        indicatorsContainer: (p) => ({ ...p, pr: 2 }),
        indicatorSeparator: (p) => ({ ...p, display: 'none' }),

        clearIndicator: (p) => ({ ...p, w: 6, h: 6 }),
        crossIcon: (p) => ({ ...p, h: 2, w: 2 }),

        dropdownIndicator: (p) => ({ ...p, px: 0, py: 0, display: allSelected && display !== 'count' ? 'none' : 'flex' }),
        downChevron: (p) => ({ ...p, h: 4, w: 4 }),
        ...(chakraStyles ?? {}),
      }}
      components={customComponents(display)}
    />
  )
})

  ; (MultipleSelectFormControlBase as any).displayName = 'MultipleSelectFormControlBase'

export const MultipleSelectFormControl = withFormControl<
  MultipleSelectFormControlComponentProps,
  string[] | number[],
  HTMLDivElement
>(MultipleSelectFormControlBase)

export type MultipleSelectFormControlProps = MultipleSelectFormControlComponentProps
